/**
 * Décor hors piste — le style de la planche voitures appliqué au monde : des
 * props PLATS (aplats + contour encre + ombre décalée dure, zéro dégradé),
 * semés autour du ruban de piste. Arbres, buissons, piles de pneus (le clin
 * d'œil auto-tamponneuse), cônes et fleurettes.
 *
 * Purement client et cosmétique (aucun effet physique), mais DÉTERMINISTE :
 * la graine dérive de la géométrie du circuit, donc tous les joueurs voient le
 * même décor au même endroit, et les captures/bancs sont reproductibles. Le
 * placement vérifie la distance réelle au ruban (closestPoint) : un prop ne
 * chevauche jamais la piste, ni une autre section qui repasse à côté.
 */

import type { SurfaceId, Track, TrackSample } from '@microligue/shared';
import { closestPoint } from '@microligue/shared';
import { isColdSurface } from './surfaces';

export type SceneryKind =
  | 'tree'
  | 'bush'
  | 'tires'
  | 'cone'
  | 'flower'
  /** Taches de SOL de la zone de fonte (neige sur l'herbe, herbe sur la neige)
   * — dessinées sous les autres props, sans contour ni ombre. */
  | 'snowpatch'
  | 'grasspatch';

export interface SceneryProp {
  x: number;
  y: number;
  kind: SceneryKind;
  /** Rayon visuel du prop (px monde) — sert aussi au cull et à l'espacement. */
  r: number;
  /** Version hivernale (section de piste voisine froide) — voir drawScenery. */
  snowy: boolean;
}

const INK = '#20222e';
const SHADOW = 'rgba(24, 26, 40, 0.14)';
/** Décalage de l'ombre plate (même langage que l'ombre du composant voiture). */
const SHADOW_DX = 4;
const SHADOW_DY = 4;
/** Marge de sécurité entre le bord du prop et le bord de la bande (px). */
const TRACK_CLEARANCE = 14;
/**
 * Zone de fonte (px d'arc de part et d'autre d'une frontière chaud/froid) : le
 * climat des props s'y MÉLANGE au tirage (50/50 sur la ligne, pur au-delà) et
 * des plaques de fonte y sont semées — la limite verdure/neige se dissout au
 * lieu de basculer net, en accord avec le fondu du manteau (render.ts).
 */
const MELT_ZONE_PX = 170;

/** mulberry32 — même générateur que shared/generator, copié pour rester local. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Graine stable dérivée de la géométrie (même circuit ⇒ même décor partout). */
function trackSeed(track: Track): number {
  const s0 = track.samples[0];
  const x = s0 ? s0.x : 0;
  const y = s0 ? s0.y : 0;
  return (
    (Math.round(track.length * 10) ^
      Math.imul(track.samples.length, 2654435761) ^
      Math.round(x * 7 + y * 13)) >>>
    0
  );
}

/**
 * Sème le décor d'un circuit. Coût build uniquement (~quelques ms).
 * `overrideSurface` : bascule de surface du circuit entier (outil admin) — le
 * décor suit le climat affiché. La séquence aléatoire est consommée à
 * l'identique quel que soit ce paramètre : mêmes emplacements, seule la
 * « tenue » (enneigée ou non) des props change.
 */
export function buildScenery(
  track: Track,
  overrideSurface: SurfaceId | null = null,
): SceneryProp[] {
  const rand = mulberry32(trackSeed(track));
  const props: SceneryProp[] = [];
  /** Une cellule de 52 px ne porte qu'un prop : espacement garanti, O(1). */
  const cells = new Set<string>();
  const samples = track.samples;
  const n = samples.length;

  // Froideur effective par échantillon, puis distance circulaire (en
  // échantillons) à la frontière chaud/froid la plus proche — la zone de fonte.
  const cold: boolean[] = new Array<boolean>(n);
  for (let i = 0; i < n; i++) {
    const s = samples[i] as TrackSample;
    cold[i] = isColdSurface(overrideSurface ?? s.surface);
  }
  const distT: number[] = new Array<number>(n).fill(Number.POSITIVE_INFINITY);
  for (let i = 0; i < n; i++) {
    if (cold[i] !== cold[(i - 1 + n) % n]) distT[i] = 0;
  }
  // Balayages avant/arrière avec rebouclage : distance circulaire exacte.
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < n; i++) {
      const viaPrev = (distT[(i - 1 + n) % n] as number) + 1;
      if (viaPrev < (distT[i] as number)) distT[i] = viaPrev;
    }
    for (let i = n - 1; i >= 0; i--) {
      const viaNext = (distT[(i + 1) % n] as number) + 1;
      if (viaNext < (distT[i] as number)) distT[i] = viaNext;
    }
  }
  const meltSamples = Math.max(1, Math.round(MELT_ZONE_PX / track.step));

  const tryPlace = (kind: SceneryKind, r: number, x: number, y: number): void => {
    const cell = `${Math.round(x / 52)}:${Math.round(y / 52)}`;
    if (cells.has(cell)) return;
    // Tiré AVANT toute décision climatique : les retours précoces restants ne
    // dépendent que de la géométrie, donc la séquence aléatoire (et tous les
    // emplacements) reste identique quelle que soit la bascule de surface.
    const jitter = rand();
    // Distance réelle au ruban — la section locale COMME toute section voisine.
    const q = closestPoint(track, { x, y });
    if (q.dist < q.halfWidth + r + TRACK_CLEARANCE) return;
    const idx = Math.round(q.s / track.step) % n;
    const baseCold = cold[idx] as boolean;
    const d = distT[idx] as number;
    let snowy = baseCold;
    let resolved = kind;
    if (resolved === 'snowpatch' || resolved === 'grasspatch') {
      // Plaque de fonte : seulement dans la zone, teinte opposée au sol local.
      if (d >= meltSamples) return;
      resolved = baseCold ? 'grasspatch' : 'snowpatch';
      snowy = false;
    } else if (d < meltSamples) {
      // Zone de fonte : le climat des props se mélange au tirage — 50/50 sur
      // la ligne, pur au-delà de la zone.
      const edge = 0.5 * (1 - d / meltSamples);
      snowy = jitter < (baseCold ? 1 - edge : edge);
    }
    // Pas de fleurettes sous la neige — le manteau blanc reste sobre.
    if (resolved === 'flower' && snowy) return;
    cells.add(cell);
    props.push({ x, y, kind: resolved, r, snowy });
  };

  // Gros props : tous les 6 échantillons (~36 px), un côté au hasard.
  for (let i = 0; i < n; i += 6) {
    if (rand() > 0.62) continue;
    const smp = samples[i];
    if (!smp) continue;
    const side = rand() < 0.5 ? -1 : 1;
    const nx = -smp.ty * side;
    const ny = smp.tx * side;
    const dist = smp.halfWidth + 34 + Math.pow(rand(), 1.4) * 190;
    const roll = rand();
    if (roll < 0.34) {
      tryPlace('tree', 15 + rand() * 9, smp.x + nx * dist, smp.y + ny * dist);
    } else if (roll < 0.68) {
      tryPlace('bush', 7 + rand() * 4, smp.x + nx * dist, smp.y + ny * dist);
    } else if (roll < 0.84) {
      tryPlace('tires', 9, smp.x + nx * dist, smp.y + ny * dist);
    } else {
      tryPlace('cone', 5, smp.x + nx * dist, smp.y + ny * dist);
    }
  }

  // Fleurettes : plus denses, plus proches du bord — de la vie sans surcharge.
  for (let i = 0; i < n; i += 4) {
    if (rand() > 0.3) continue;
    const smp = samples[i];
    if (!smp) continue;
    const side = rand() < 0.5 ? -1 : 1;
    const nx = -smp.ty * side;
    const ny = smp.tx * side;
    const dist = smp.halfWidth + 22 + rand() * 110;
    tryPlace('flower', 3, smp.x + nx * dist, smp.y + ny * dist);
  }

  // Plaques de fonte : semées en dernier (elles ne volent la cellule d'aucun
  // prop), densité décroissante avec la distance à la frontière. Les tirages
  // sont TOUJOURS consommés, même hors zone ou sans la moindre frontière
  // (bascule de surface) : séquence stable, mêmes décors.
  for (let i = 0; i < n; i += 2) {
    const gate = rand();
    const side = rand() < 0.5 ? -1 : 1;
    const spread = rand();
    const size = rand();
    const d = distT[i] as number;
    if (d >= meltSamples) continue;
    const smp = samples[i];
    if (!smp) continue;
    const closeness = 1 - d / meltSamples;
    if (gate > 0.85 * Math.pow(closeness, 1.5)) continue;
    const nx = -smp.ty * side;
    const ny = smp.tx * side;
    const dist = smp.halfWidth + 8 + Math.pow(spread, 1.2) * 200;
    tryPlace('snowpatch', 4 + size * 7, smp.x + nx * dist, smp.y + ny * dist);
  }

  return props;
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
}

/** Dessine les props visibles dans la fenêtre monde donnée (cull par bbox).
 * Deux passes : les taches de SOL d'abord (semées en dernier dans le tableau,
 * mais posées dessous), puis les props qui vivent dessus. */
export function drawScenery(
  ctx: CanvasRenderingContext2D,
  props: SceneryProp[],
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): void {
  drawSceneryPass(ctx, props, minX, minY, maxX, maxY, true);
  drawSceneryPass(ctx, props, minX, minY, maxX, maxY, false);
}

function drawSceneryPass(
  ctx: CanvasRenderingContext2D,
  props: SceneryProp[],
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
  groundPass: boolean,
): void {
  for (const p of props) {
    if ((p.kind === 'snowpatch' || p.kind === 'grasspatch') !== groundPass) continue;
    const pad = p.r + SHADOW_DX + 3;
    if (p.x + pad < minX || p.x - pad > maxX || p.y + pad < minY || p.y - pad > maxY) continue;
    switch (p.kind) {
      case 'tree': {
        // Canopée à lobes (trois cercles), détourée par une SILHOUETTE encre
        // légèrement plus large — le contour du kit sans couture interne.
        // Version enneigée : canopée blanche, ombrage bleuté au lieu du reflet.
        const lobes: [number, number, number][] = [
          [0, -p.r * 0.14, p.r],
          [-p.r * 0.58, p.r * 0.3, p.r * 0.62],
          [p.r * 0.56, p.r * 0.34, p.r * 0.56],
        ];
        ctx.fillStyle = SHADOW;
        circle(ctx, p.x + SHADOW_DX, p.y + SHADOW_DY, p.r * 1.12);
        ctx.fill();
        ctx.fillStyle = INK;
        for (const [dx, dy, r] of lobes) {
          circle(ctx, p.x + dx, p.y + dy, r + 2.2);
          ctx.fill();
        }
        ctx.fillStyle = p.snowy ? '#f3f7fb' : '#2f9e5b';
        for (const [dx, dy, r] of lobes) {
          circle(ctx, p.x + dx, p.y + dy, r);
          ctx.fill();
        }
        if (p.snowy) {
          ctx.fillStyle = 'rgba(91, 118, 156, 0.18)';
          circle(ctx, p.x + p.r * 0.26, p.y + p.r * 0.32, p.r * 0.5);
        } else {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
          circle(ctx, p.x - p.r * 0.3, p.y - p.r * 0.38, p.r * 0.45);
        }
        ctx.fill();
        break;
      }
      case 'bush': {
        ctx.fillStyle = SHADOW;
        circle(ctx, p.x + SHADOW_DX * 0.7, p.y + SHADOW_DY * 0.7, p.r);
        ctx.fill();
        ctx.fillStyle = p.snowy ? '#e9f0f7' : '#35c56f';
        circle(ctx, p.x, p.y, p.r);
        ctx.fill();
        ctx.strokeStyle = INK;
        ctx.lineWidth = 2;
        ctx.stroke();
        if (p.snowy) {
          ctx.fillStyle = 'rgba(91, 118, 156, 0.16)';
          circle(ctx, p.x + p.r * 0.25, p.y + p.r * 0.28, p.r * 0.45);
        } else {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
          circle(ctx, p.x - p.r * 0.3, p.y - p.r * 0.3, p.r * 0.45);
        }
        ctx.fill();
        break;
      }
      case 'tires': {
        ctx.fillStyle = SHADOW;
        circle(ctx, p.x + SHADOW_DX * 0.8, p.y + SHADOW_DY * 0.8, p.r);
        ctx.fill();
        ctx.fillStyle = INK;
        circle(ctx, p.x, p.y, p.r);
        ctx.fill();
        // Nuance PROPRE au décor : ne doit recouper aucune SURFACE_COLORS (le
        // test de bascule de surface filtre les aplats par cette palette).
        ctx.fillStyle = '#5b6274';
        circle(ctx, p.x, p.y, p.r * 0.68);
        ctx.fill();
        ctx.fillStyle = INK;
        circle(ctx, p.x, p.y, p.r * 0.34);
        ctx.fill();
        break;
      }
      case 'cone': {
        ctx.fillStyle = '#ff8a2a';
        circle(ctx, p.x, p.y, p.r);
        ctx.fill();
        ctx.strokeStyle = INK;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = '#f7f3e8';
        circle(ctx, p.x, p.y, p.r * 0.42);
        ctx.fill();
        break;
      }
      case 'flower': {
        ctx.fillStyle = '#f7f3e8';
        circle(ctx, p.x, p.y, p.r);
        ctx.fill();
        ctx.fillStyle = '#ffc93c';
        circle(ctx, p.x, p.y, p.r * 0.45);
        ctx.fill();
        break;
      }
      // Taches de sol de la zone de fonte : deux lobes plats, sans contour ni
      // ombre — le même langage que le manteau, pas celui des objets posés.
      case 'snowpatch': {
        ctx.fillStyle = '#e9f0f7';
        circle(ctx, p.x, p.y, p.r);
        ctx.fill();
        circle(ctx, p.x + p.r * 0.9, p.y + p.r * 0.4, p.r * 0.62);
        ctx.fill();
        break;
      }
      case 'grasspatch': {
        ctx.fillStyle = '#4a9464';
        circle(ctx, p.x, p.y, p.r);
        ctx.fill();
        circle(ctx, p.x - p.r * 0.85, p.y + p.r * 0.35, p.r * 0.6);
        ctx.fill();
        break;
      }
    }
  }
}
